"use client";

import { useEffect, useState } from "react";
import type AWSMarketingCookieConsent from "@amzn/awsmcc";
import { useAsync } from "./useAsync";

// https://code.amazon.com/packages/AWSMarketingDynamicCookieConsent/trees/mainline#usage

type CookieModule = typeof AWSMarketingCookieConsent;

export type CookieConsent = ReturnType<
  CookieModule["getCookieConsentValidatorInstance"]
>;

let cookieConsentInstance: CookieConsent;
let cookieModuleImportPromise: Promise<CookieModule>;

export const isServer = () => {
  return typeof window === "undefined";
};

export const importCookieModule = async () => {
  return !isServer() && (cookieModuleImportPromise ??= import("@amzn/awsmcc"));
};

/** Gets the cookie consent API instance */
export function useCookieConsent(): CookieConsent | undefined {
  const { isDone, result } = useAsync(importCookieModule);
  return isDone && result
    ? (cookieConsentInstance ??= result.getCookieConsentValidatorInstance())
    : undefined;
}

/** Get the full Shortbread API */
export function useShortbread(): CookieConsent["_shortbread"] | undefined {
  const cookieConsent = useCookieConsent();
  return cookieConsent?._shortbread;
}

/** Gets the current access and updates it when things change */
export function useCookieHasConsent(...cookieNames: string[]) {
  const cookieConsent = useCookieConsent();
  const [hasConsent, setConsent] = useState(
    !!cookieConsent?.hasConsent(cookieNames),
  );
  useEffect(() => {
    cookieConsent?.access(
      cookieNames,
      () => setConsent(true),
      () => setConsent(false),
    );
  }, [cookieNames, cookieConsent]);
  return hasConsent;
}

/** Open the Cookie preferences modal */
export function useCookiePreferences() {
  const shortbread = useShortbread();
  return shortbread?.customizeCookies;
}

export function usePrivacyPreferences() {
  const shortbread = useShortbread();
  return {
    show: () => shortbread?.showCcbaPreferenceModal?.(),
  };
}
